/* ============================================================
   ERO CONTROL EFFECTS (Launch Workbook 6.14.30)

   Drives the four control effects styled in
   assets/component-control-fx.css. It never selects anything itself:
   each section keeps its own click and pointerup logic, and this file
   only reacts afterwards.
   1. Selector tiles: after a tap, click, Enter or Space that leaves the
      tile selected, insert an aria-hidden sweep layer on first use and
      play it once.
   2. Quantity numbers: the buy box, sticky bar and buy module scripts
      write the number with textContent. A MutationObserver wraps the new
      number in <span class="ero-qty-tick"> and plays the tick when the
      value actually changed. The wrap is a child of the same node, so
      textContent reads (the sticky bar's sync) still see the plain number.
      The cart drawer's number is an <input>; its change event plays a
      flash on the field.
   3. FAQ cards: is-fx-press on the item while pressed (any pointer),
      is-fx-cancel when a scroll cancels the press; the arrow pops on each
      toggle. The lit layer is inserted on first hover or press.
   4. Mobile menu rows: is-fx-press / is-fx-cancel on the link while
      pressed, then is-fx-go and a 400ms wait so the underline finishes
      before the page changes. The desktop top bar underline is CSS
      :hover only.
   Pointer Events only, delegated from the document, passive listeners.
   ============================================================ */
(function () {
  'use strict';

  var TILE = '.bb-flavor, .bb-plan, .ero-color-card, .ero-sp-xsell__tile, ' +
    '.ero-sp-xsell__swatch--tile, .ero-collection__tile';
  // Taps inside the Subscribe card's frequency picker are not tile picks.
  var TILE_SKIP = '[data-frequency], [role="listbox"]';
  var QTY = '[data-qty-value], [data-satc-qty-value], [data-qty-display]';
  var PRESS = '.ero-pdp-faq__trigger, .ero-drawer__link';

  function replay(el, cls) {
    el.classList.remove(cls);
    void el.offsetWidth; // restart the one-pass animation
    el.classList.add(cls);
  }

  /* ── 1. Selector tile sweep ── */
  function isSelected(el) {
    return el.classList.contains('is-active') ||
      el.classList.contains('is-selected') ||
      el.getAttribute('aria-pressed') === 'true' ||
      el.getAttribute('aria-checked') === 'true';
  }

  function isBlocked(el) {
    return el.disabled ||
      el.getAttribute('aria-disabled') === 'true' ||
      el.getAttribute('data-unavailable') === 'true' ||
      el.classList.contains('is-disabled');
  }

  function sweep(tile) {
    var fx = tile._eroCfx;
    if (!fx || fx.parentNode !== tile) {
      fx = document.createElement('span');
      fx.className = 'ero-cfx';
      fx.setAttribute('aria-hidden', 'true');
      var sheen = document.createElement('span');
      sheen.className = 'ero-cfx__sheen';
      fx.appendChild(sheen);
      tile.appendChild(fx);
      tile._eroCfx = fx;
    }
    var now = Date.now();
    if (tile._eroCfxAt && now - tile._eroCfxAt < 400) return; // one pass per pick
    tile._eroCfxAt = now;
    replay(fx, 'is-sweep');
  }

  function tilePicked(target) {
    if (!target || !target.closest || target.closest(TILE_SKIP)) return;
    var tile = target.closest(TILE);
    if (!tile || isBlocked(tile)) return;
    // The section's own handler has run by the next frame.
    requestAnimationFrame(function () {
      if (isSelected(tile)) sweep(tile);
    });
  }

  // Most pickers select on pointerup, the collection tiles on click; both
  // are watched and sweep() keeps it to one pass per pick.
  document.addEventListener('pointerup', function (e) {
    tilePicked(e.target);
  }, { passive: true });

  document.addEventListener('click', function (e) {
    tilePicked(e.target);
  }, { passive: true });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') tilePicked(e.target);
  }, { passive: true });

  /* ── 2. Quantity tick ── */
  function wrapQty(node) {
    var first = node.firstChild;
    if (first && first.nodeType === 1 && first.classList.contains('ero-qty-tick') &&
        node.childNodes.length === 1) {
      return; // already wrapped (our own mutation)
    }
    var value = node.textContent.trim();
    var changed = node._eroQty !== undefined && node._eroQty !== value;
    node._eroQty = value;
    var span = document.createElement('span');
    span.className = 'ero-qty-tick';
    span.textContent = value;
    node.textContent = '';
    node.appendChild(span);
    if (changed) replay(span, 'is-tick');
  }

  function watchQty(node) {
    if (node._eroQtyWatch) return;
    node._eroQtyWatch = true;
    node._eroQty = node.textContent.trim();
    new MutationObserver(function () { wrapQty(node); })
      .observe(node, { childList: true, characterData: true, subtree: true });
  }

  function watchAll() {
    document.querySelectorAll(QTY).forEach(watchQty);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchAll);
  } else {
    watchAll();
  }

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (input && input.classList && input.classList.contains('ero-cart__qty-input')) {
      replay(input, 'is-tick');
    }
  }, { passive: true });

  /* ── 3 + 4. Press states: FAQ cards and mobile menu rows ── */
  var pressed = null;
  var cancelTimer = null;

  function hostOf(el) {
    return el.classList.contains('ero-pdp-faq__trigger') ? el.closest('.ero-pdp-faq__item') : el;
  }

  function ensureLit(item) {
    if (!item || !item.classList.contains('ero-pdp-faq__item')) return;
    if (item._eroCfxLit && item._eroCfxLit.parentNode === item) return;
    var lit = document.createElement('span');
    lit.className = 'ero-cfx-lit';
    lit.setAttribute('aria-hidden', 'true');
    item.insertBefore(lit, item.firstChild);
    item._eroCfxLit = lit;
  }

  function release(cancelled) {
    if (!pressed) return;
    var host = pressed;
    pressed = null;
    if (cancelled) {
      host.classList.add('is-fx-cancel');
      clearTimeout(cancelTimer);
      cancelTimer = setTimeout(function () { host.classList.remove('is-fx-cancel'); }, 200);
    }
    host.classList.remove('is-fx-press');
  }

  document.addEventListener('pointerover', function (e) {
    if (e.pointerType !== 'mouse') return;
    var item = e.target.closest && e.target.closest('.ero-pdp-faq__item');
    if (item) ensureLit(item);
  }, { passive: true });

  document.addEventListener('pointerdown', function (e) {
    var el = e.target.closest && e.target.closest(PRESS);
    if (!el) return;
    var host = hostOf(el);
    if (!host) return;
    release(false);
    ensureLit(host);
    host.classList.remove('is-fx-cancel');
    host.classList.add('is-fx-press');
    pressed = host;
  }, { passive: true });

  // Pop the FAQ arrow on each toggle. The section toggles on pointerup
  // and on Enter / Space, so this listens to the same two.
  function pop(target) {
    var trigger = target && target.closest && target.closest('.ero-pdp-faq__trigger');
    if (!trigger) return;
    var chevron = trigger.querySelector('.ero-pdp-faq__chevron');
    if (chevron) replay(chevron, 'is-fx-pop');
  }

  document.addEventListener('pointerup', function (e) {
    if (pressed && pressed.classList.contains('ero-pdp-faq__item')) pop(e.target);
    release(false);
  }, { passive: true, capture: true });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') pop(e.target);
  }, { passive: true });

  document.addEventListener('pointercancel', function () { release(true); }, { passive: true, capture: true });

  /* ── 4b. Mobile menu: let the underline finish before navigating ──
     A menu tap navigates at once, so the line only flashed. A plain touch
     tap on a same-tab link now waits 400ms (the 380ms draw) and then goes.
     Mouse clicks, modifier keys, new-tab links and other origins are left
     alone. The bfcache restore clears the held line. */
  var lastPointer = 'mouse';
  var going = false;

  document.addEventListener('pointerdown', function (e) {
    lastPointer = e.pointerType || 'mouse';
  }, { passive: true, capture: true });

  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a.ero-drawer__link');
    if (!link || e.defaultPrevented || lastPointer === 'mouse') return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (link.target && link.target !== '_self') return;
    if (link.origin !== window.location.origin) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    e.preventDefault();
    if (going) return;
    going = true;
    link.classList.add('is-fx-go');
    var href = link.href;
    setTimeout(function () { window.location.assign(href); }, 400);
  });

  window.addEventListener('pageshow', function () {
    going = false;
    document.querySelectorAll('.ero-drawer__link.is-fx-go').forEach(function (l) {
      l.classList.remove('is-fx-go');
    });
  });

  // Chevron pop must not fight the press squeeze once it has played.
  document.addEventListener('animationend', function (e) {
    if (e.animationName === 'eroCfxPop') e.target.classList.remove('is-fx-pop');
  }, { passive: true });
})();
